import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** One real input preserves paste, password-manager autofill and screen-reader navigation. */
export function CodeInput({
  value,
  onChange,
  allowBackup = true,
  invalid,
  id,
  testId,
  autoSubmit = false,
}: {
  value: string;
  onChange: (value: string) => void;
  allowBackup?: boolean;
  invalid?: boolean;
  id?: string;
  testId?: string;
  /** Kad je unesena 6. cifra, šalje formu u kojoj je polje (samo kod iz aplikacije, ne rezervni kod). */
  autoSubmit?: boolean;
}) {
  const { t } = useTranslation();
  const [backup, setBackup] = useState(false);
  const [focused, setFocused] = useState(false);
  const [position, setPosition] = useState(0);
  const change = (text: string, form: HTMLFormElement | null) => {
    const next = text.replace(/\s/g, '');
    setPosition(Math.min(next.length, 5));
    if (allowBackup && !/^\d{0,6}$/.test(next)) {
      setBackup(true);
      onChange(next);
      return;
    }
    const digits = backup ? next : next.replace(/\D/g, '').slice(0, 6);
    onChange(digits);
    // Poslije re-rendera, da forma pročita novu vrijednost; samo pri prelasku na 6 cifara (ne pri ponovnom fokusu).
    if (autoSubmit && !backup && digits.length === 6 && value.length < 6 && form)
      setTimeout(() => form.requestSubmit(), 0);
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="relative rounded-md">
        {!backup ? (
          <div className="grid grid-cols-6 gap-2" aria-hidden="true">
            {Array.from({ length: 6 }, (_, i) => (
              <span
                key={i}
                data-active={focused && position === i ? 'true' : undefined}
                className={`grid h-14 min-w-0 place-items-center rounded-md border bg-surface font-mono text-2xl font-semibold ${invalid ? 'border-danger' : 'border-line2'} ${focused && position === i ? 'ring-2 ring-accent' : ''}`}
              >
                {value[i] ?? ''}
              </span>
            ))}
          </div>
        ) : null}
        <input
          id={id}
          data-testid={testId}
          aria-label={t('panel:platform.login.code')}
          aria-invalid={invalid || undefined}
          autoComplete="one-time-code"
          autoFocus
          inputMode={backup ? 'text' : 'numeric'}
          value={value}
          maxLength={32}
          onChange={(e) => change(e.target.value, e.currentTarget.form)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onSelect={(e) => setPosition(Math.min(e.currentTarget.selectionStart ?? value.length, 5))}
          onClick={(e) => {
            if (backup) return;
            const bounds = e.currentTarget.getBoundingClientRect();
            if (!bounds.width) return;
            const cell = Math.max(
              0,
              Math.min(5, Math.floor(((e.clientX - bounds.left) / bounds.width) * 6)),
            );
            const cursor = Math.min(cell, value.length);
            e.currentTarget.setSelectionRange(cursor, cursor);
            setPosition(cursor);
          }}
          className={
            backup
              ? 'h-14 w-full rounded-md border border-line2 bg-surface px-4 font-mono text-lg text-text outline-none focus:ring-2 focus:ring-accent'
              : 'absolute inset-0 h-full w-full cursor-text opacity-0 outline-none ring-0 focus:outline-none focus:ring-0'
          }
        />
      </div>
      {allowBackup ? (
        <button
          type="button"
          className="self-start text-[13px] font-semibold text-accent"
          onClick={() => {
            setBackup(!backup);
            onChange('');
          }}
        >
          {t(`panel:platform.login.${backup ? 'useAuthenticator' : 'useBackupCode'}`)}
        </button>
      ) : null}
    </div>
  );
}
